/* eslint-disable jsx-a11y/media-has-caption */
import React, { createRef, memo, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { capitalize, isEqual, sortBy } from 'lodash';
import actions from '@src/redux/actions';
import apis from '@src/apis';
import { CircularProgress, DialogContent } from '@mui/material';
import DialogDictionary from './DialogDictionary';
import FormAddDictionary from './FormAddDictionary';
import ListDictionary from './ListDictionary';
import Tip from './Tip';

const NewDictionary = memo(({ open, onClose, currentItem }) => {
  const { t } = useTranslation();
  const RECENTLY = 'RECENTLY';
  const ATOZ = 'ATOZ';
  const ZTOA = 'ZTOA';
  const [selectedItem, setSeletedItem] = useState([]);
  const [wordsData, setWordsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [sort, setSort] = useState(RECENTLY);

  const { relateDataRequest } = useSelector((state) => state.synthesisRequest);
  const { audioLink, requestPreviewId, isPlaying, playAudioButtonId } =
    useSelector((state) => state.dictionary);
  const audioRef = createRef(null);
  const dispatch = useDispatch();

  const fetchDictionary = async (blockLoading = false) => {
    if (!blockLoading) {
      setLoading(true);
    }
    const data = await apis.voices.getDictionary();
    if (data?.status) {
      const newWords = data.result?.words || [];
      setWordsData(newWords);
    }
    setLoading(false);
    return data.result?.words || [];
  };

  const handleGetPresignedAudioUrl = async (keyId) => {
    try {
      const res = await apis.requests.getPresignedAudioUrl(keyId);
      if (res?.status) {
        const audioUrl = res.result?.audio;
        dispatch(actions.dictionary.updatePlayAudioLink(audioUrl));
        return;
      }
      dispatch(
        actions.noti.push({
          severity: 'error',
          message: 'downloadError',
        }),
      );
      dispatch(actions.dictionary.updatePlayAudioLink(audioLink));
      return;
    } catch (error) {
      dispatch(
        actions.noti.push({
          severity: 'error',
          message: 'downloadError',
        }),
      );
      dispatch(actions.dictionary.updatePlayAudioLink(audioLink));
    }
  };

  const handleClose = () => {
    dispatch(actions.dictionary.resetPreviewAudio());
    // Set timeout to wait for reset preview audio
    setTimeout(() => onClose(), 100);
  };

  useEffect(() => {
    fetchDictionary();
  }, [open]);

  const wordsView = useMemo(() => {
    const data = wordsData?.filter(
      (item) =>
        item?.pronunciation
          .trim()
          .toLowerCase()
          ?.includes(searchText.trim().toLowerCase()) ||
        item?.word
          .trim()
          .toLowerCase()
          ?.includes(searchText.trim().toLowerCase()),
    );

    if (sort === RECENTLY) {
      return data;
    }
    if (sort === ATOZ) {
      return sortBy(data, (item) => item?.word);
    }
    if (sort === ZTOA) {
      return sortBy(data, (item) => item?.word).reverse();
    }
    return data;
  }, [wordsData, sort, searchText]);

  const optionsFilter = {
    [RECENTLY]: t('recently'),
    [ATOZ]: t('fromAToZ'),
    [ZTOA]: t('fromZToA'),
  };

  useEffect(() => {
    if (isPlaying && audioLink && audioRef?.current && playAudioButtonId) {
      audioRef.current.src = audioLink;
      audioRef?.current?.load();

      audioRef.current.play().catch((error) => {
        // eslint-disable-next-line no-console
        console.log(`Error when play audio ${error}`);
        const ERROR_DUE_TO_EXPIRED_AUDIO_LINK = 'NotSupportedError';
        if (error.name === ERROR_DUE_TO_EXPIRED_AUDIO_LINK) {
          handleGetPresignedAudioUrl(requestPreviewId);
        }
      });
    }
  }, [isPlaying, audioLink, playAudioButtonId]);

  const onTextChangeWhenAddWord = (firstWord) => {
    const isChange = relateDataRequest?.text
      ?.toLowerCase()
      .includes(firstWord?.word?.toLowerCase());
    if (isChange) {
      dispatch(
        actions.synthesisRequest.updateDataRelatedRequest({
          ...relateDataRequest,
          wordChange: true,
        }),
      );
    }
  };
  useEffect(() => {
    if (!Object.keys(relateDataRequest)?.length) return;
    const isEqualDict = relateDataRequest?.pronunciationDict?.every((dic) =>
      wordsData?.some((w) => isEqual(w, dic)),
    );
    if (!isEqualDict) {
      dispatch(
        actions.synthesisRequest.updateDataRelatedRequest({
          ...relateDataRequest,
          pronunciationDict: [],
        }),
      );
    }
  }, [relateDataRequest, wordsData]);
  return (
    <DialogDictionary
      title={capitalize(t('pronunciation'))}
      width={640}
      open={open}
      onClose={handleClose}
    >
      <DialogContent classes={{ root: 'dialog-content' }}>
        <FormAddDictionary
          onTextChangeWhenAddWord={onTextChangeWhenAddWord}
          wordsData={wordsData}
          words={wordsView}
          fetchDictionary={fetchDictionary}
          currentItem={currentItem}
        />
        <ListDictionary>
          {wordsData?.length && !loading ? (
            <>
              {selectedItem?.length > 0 ? (
                <ListDictionary.Actions
                  onSelectedItem={setSeletedItem}
                  words={wordsView}
                  wordsData={wordsData}
                  fetchDictionary={fetchDictionary}
                  selectedItem={selectedItem}
                  onTextChangeWhenDeleteWord={() => {
                    const findWordInText = selectedItem?.find((w) =>
                      relateDataRequest?.text?.includes(w?.word),
                    );
                    onTextChangeWhenAddWord(findWordInText);
                  }}
                />
              ) : (
                <ListDictionary.FilterDictionary
                  onSearch={setSearchText}
                  sort={sort}
                  onSort={setSort}
                  optionsFilter={optionsFilter}
                  searchText={searchText}
                />
              )}
              <ListDictionary.ListData
                words={wordsView}
                wordsData={wordsData}
                loading={loading}
                selectedItem={selectedItem}
                onSelectedItem={setSeletedItem}
                fetchDictionary={fetchDictionary}
                onTextChangeWhenAddWord={onTextChangeWhenAddWord}
              />
            </>
          ) : (
            <>
              {loading && (
                <div className="list-container">
                  <div className="loading_container">
                    <CircularProgress
                      value={75}
                      className="loading-load"
                      size="24px"
                    />
                  </div>
                </div>
              )}
              {!loading && (
                <div className="list-container">
                  <Tip hiddenClose />
                </div>
              )}
            </>
          )}
        </ListDictionary>
      </DialogContent>
    </DialogDictionary>
  );
});

export default NewDictionary;
